/* ScheduleVeilRenderer.js */

"use strict";
const _ = require("lodash");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const ScheduleComponent = require("@gluenetworks/schedule_component");
const NotificationsComponent = require("@gluenetworks/notification_component");

const InputCheckbox = require("@gluenetworks/steve_ui_core").Control.Boolean.InputCheckbox;

const InputRadioControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .InputRadio;

class ScheduleVeilRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("schedule-veil-gl"); //TODO Setup the root Element
        this._rootElem.component = this._model;
        if (this.model.darkTheme) {
            this._rootElem.classList.add("dark");
        }

        this._confirmButton = new ButtonControl({
            label: "Save",
            darkTheme: this.model.darkTheme,
            theme: "additive",
            active: true,
        });

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: this.model.darkTheme,
        });

        this._enableSchedule = new InputCheckbox({
            label: "Enable Schedule",
            labelShown: true,
            value: this._model._scheduleEnabled || false,
            darkTheme: true,
        });
        this._enableSchedule._renderer._rootElem.classList.add("enable-checkbox");

        let allowRepeat = _.isUndefined(this._model._scheduleRepeat)
            ? true
            : this._model._scheduleRepeat;
        this._scheduleComponent = new ScheduleComponent(
            this._model._schedulePassed ? this._model._schedule : undefined,
            {
                allowOne: true,
                allowRepeat: allowRepeat,
                pollOnly: false,
                availOnly: false,
                firstTime: this.model.firstTime,
            }
        );
        this._scheduleComponent.theme = "dark";

        this._addNewDevices = new InputCheckbox({
            label: "Automatically add new devices to inventory",
            labelShown: true,
            value: this._model._schedulePassed ? this._model._addNewDevices : false,
            darkTheme: true,
        });
        this._addNewDevices._renderer._rootElem.classList.add("add-new-devices-checkbox");

        this._notificationsComponent = new NotificationsComponent(
            this.model.logger,
            this.model.userSession,
            {
                darkTheme: this.model.darkTheme,
                title: "Notify on Completion",
                workType: this.model.workType,
            },
            this._model._schedulePassed ? this._model._scheduleNotifications : undefined
        );

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = this._model.titleText;

        this._buttonContainer = document.createElement("button-container");

        this._msgComponent = document.createElement("msg");

        this._reportTypeSel = new InputRadioControl({
            labelShown: true,
            horizontal: true,
            options: [
                {
                    label: "CSV",
                    value: "csv",
                },
                {
                    label: "PDF",
                    value: "pdf",
                },
                {
                    label: "JSON",
                    value: "json",
                },
            ],
            value: "csv",
            label: "Report format",
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._confirmButton.on(
            "UserClick",
            (this._onConfirmButtonClick = this._onConfirmButtonClick.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
        this._scheduleComponent.renderer.rootElement.addEventListener(
            "click",
            (this._onSchedClick = this._onSchedClick.bind(this)),
            true
        );
        this._notificationsComponent.on(
            "ValidationStateChanged",
            (this._onValidationStateChanged = this._onValidationStateChanged.bind(this))
        );
        if (this._model._soln === "DE") {
            this._reportTypeSel.on(
                "UserValue",
                (this._onReportTypeSelChange = this._onReportTypeSelChange.bind(this))
            );
        }

        this._enableSchedule.on(
            "UserValue",
            (this._onEnableClick = this._onEnableClick.bind(this))
        );
    }

    _unregisterListeners() {
        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._scheduleComponent.renderer.rootElement.removeEventListener(
            "click",
            this._onSchedClick
        );
        this._notificationsComponent.removeListener(
            "ValidationStateChanged",
            this._onValidationStateChanged
        );
        if (this._model._soln === "DE") {
            this._reportTypeSel.removeAllListeners("UserValue");
        }
    }

    _onConfirmButtonClick() {
        if (!this._enableSchedule.getValue()) {
            this._callSaveSchedule(true);
        }
        let validationErrors = this._scheduleComponent.validateSchedule();
        if (_.isEmpty(validationErrors)) {
            this._callSaveSchedule();
        } else {
            let msg = "";
            validationErrors.forEach((validationMsg) => {
                msg += `<p>${validationMsg}</p>`;
            });
            this._msgComponent.innerHTML = msg;
        }
    }

    _onCancelButtonClick() {
        this._callCancelSchedule();
    }

    _onSchedClick() {
        this._msgComponent.innerHTML = "";
    }

    _checkForImpliedConfirm(event) {
        if (event.keyCode === 13) {
            this._callStartCapture();
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._scheduleComponent.shutdown(),
            this._notificationsComponent.shutdown(),
            this._confirmButton.shutdown(),
            this._cancelButton.shutdown(),
        ];

        if (this._model._soln === "DE") {
            promArr.push(this._reportTypeSel.shutdown());
        }

        return Promise.all(promArr).then(
            () => {
                this._confirmButton = undefined;
                this._cancel = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._notificationsComponent = undefined;
                this._reportTypeSel = undefined;
                this._msgComponent = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._model = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "ScheduleVeilRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                let renderPromises = [
                    this._scheduleComponent.renderer.render(),
                    this._notificationsComponent.renderer.render(),
                ];
                return Promise.all(renderPromises).then(() => {
                    this._rootElem.appendChild(this._titleComponent);
                    this._rootElem.appendChild(this._enableSchedule.renderer._rootElem);
                    this._rootElem.appendChild(this._scheduleComponent.renderer.rootElement);

                    this._rootElem.appendChild(this._addNewDevices._renderer._rootElem);

                    this._rootElem.appendChild(this._notificationsComponent.renderer.rootElement);
                    if (this._model._soln === "DE") {
                        this._rootElem.appendChild(
                            this._createControlWithLabel(this._reportTypeSel, "Report format")
                        );
                    }
                    this._rootElem.appendChild(this._buttonContainer);
                    this._rootElem.appendChild(this._msgComponent);

                    this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                    this._buttonContainer.appendChild(this._confirmButton.renderer.rootElement);

                    this._enableDisableSchedule();
                    return Promise.resolve();
                });
            }
            resolve();
        });
    }

    _enableDisableSchedule() {
        if (this._enableSchedule.getValue()) {
            if (this.isScheduleInPast(this._scheduleComponent.getSchedule().on)) {
                this._scheduleComponent.setSchedule(ScheduleComponent.defaultValue());
            }

            this._scheduleComponent.renderer.rootElement.classList.remove("disabled-form");
            this._addNewDevices.renderer.rootElement.classList.remove("disabled-form");
            this._notificationsComponent.renderer.rootElement.classList.remove("disabled-form");
        } else {
            this._scheduleComponent.renderer.rootElement.classList.add("disabled-form");
            this._addNewDevices.renderer.rootElement.classList.add("disabled-form");
            this._notificationsComponent.renderer.rootElement.classList.add("disabled-form");
        }
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    _enableComfirmButtons(enable) {
        this._confirmButton.disabled = !enable;
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onValidationStateChanged(isValid) {
        this._enableComfirmButtons(isValid);
    }

    _onReportTypeSelChange(val) {
        this._model.reportFormat = val;
    }

    _onEnableClick() {
        this._enableDisableSchedule();
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _callSaveSchedule(justDisable = false) {
        this._model.saveSchedule({
            schedule: justDisable ? undefined : this._scheduleComponent.getSchedule(),
            workInput: justDisable
                ? undefined
                : this._notificationsComponent.getNotificationsWorkInputObject(),
            scheduleEnabled: this._enableSchedule.getValue(),
            autoAddDevices: justDisable ? undefined : this._addNewDevices.getValue(),
        });
    }

    _callCancelSchedule() {
        this._model.cancelSchedule({userRejected: true});
    }

    _createControlWithLabel(control, label = "") {
        const elem = document.createElement("div");
        elem.classList.add("control-inp");
        elem.classList.add(label.toLowerCase().replace(" ", "_").replace(/\W/g, ""), "label");
        const labelEl = document.createElement("label");
        labelEl.innerText = label;
        elem.appendChild(labelEl);
        elem.appendChild(control.renderer.rootElement);
        return elem;
    }

    isScheduleInPast(schedule) {
        if (!_.isEmpty(schedule)) {
            const now = new Date();
            const scheduleDate = new Date(
                schedule.year,
                schedule.month,
                schedule.date,
                schedule.hour,
                schedule.minute
            );

            return scheduleDate < now;
        }

        return false;
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = ScheduleVeilRenderer;
